import type { RefObject } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as BackIcon,
  Menu as MenuIcon,
  PlayArrow as RunIcon,
  ChevronRight as DetailsIcon,
  ChevronLeft as DetailsOpenIcon,
  HelpOutline as GuideIcon,
  Rule as ConditionStudioIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ReportProblem as CriticalIcon,
} from '@mui/icons-material';
import { borderRadius, layoutGap } from '../../../theme/theme';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';

import {
  getWorkspaceActionButtonSx,
  getWorkspaceCommandGroupSx,
  getWorkflowWorkspacePalette,
  getWorkspaceSubpageHeaderSurfaceSx,
  getWorkspaceIconButtonSx,
} from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import {
  resolveValidationHeaderStatus,
  type ValidationHeaderStatusIcon,
} from '../lib/validationHeaderModel';
import { validationCopy } from '../copy/validationCopy';
import { CaptionText, CardTitle } from '../../../shared/ui/typography';
import ValidationHeaderConfigurationActions from './ValidationHeaderConfigurationActions';

interface ValidationHeaderProps {
  documentName?: string;
  lastValidationLabel?: string;
  unresolvedFamilyCount: number;
  totalIssueCount: number;
  criticalCount: number;
  errorCount: number;
  warningCount: number;
  loading: boolean;
  onRunValidation: () => void;
  onShowConfig: () => void;
  onOpenConditionStudio?: () => void;
  canConfigureMappings: boolean;
  selectedValidationCount: number;
  totalValidationCount: number;
  onOpenRail?: () => void;
  onToggleDetail?: () => void;
  showRailToggle?: boolean;
  showDetailToggle?: boolean;
  detailOpen?: boolean;
  focusedSystemName?: string;
  focusedElementCount?: number;
  onClearFocusedSystem?: () => void;
  capabilityNote?: string;
  onOpenGuide?: () => void;
  showBackButton?: boolean;
  onGoBack?: () => void;
  embedded?: boolean;
  tourRefs?: {
    railToggleRef?: RefObject<HTMLButtonElement | null>;
    detailToggleRef?: RefObject<HTMLButtonElement | null>;
    runContextRef?: RefObject<HTMLDivElement | null>;
    actionsRef?: RefObject<HTMLDivElement | null>;
    guideActionRef?: RefObject<HTMLButtonElement | null>;
    conditionStudioActionRef?: RefObject<HTMLButtonElement | null>;
    configureActionRef?: RefObject<HTMLButtonElement | null>;
    runActionRef?: RefObject<HTMLButtonElement | null>;
  };
}
function renderStatusIcon(icon: ValidationHeaderStatusIcon) {
  const iconSx = { fontSize: 16 };

  switch (icon) {
    case 'success':
      return <SuccessIcon sx={iconSx} />;
    case 'critical':
      return <CriticalIcon sx={iconSx} />;
    case 'error':
      return <ErrorIcon sx={iconSx} />;
    case 'warning':
    default:
      return <WarningIcon sx={iconSx} />;
  }
}
export default function ValidationHeader({
  documentName,
  lastValidationLabel,
  unresolvedFamilyCount,
  totalIssueCount,
  criticalCount,
  errorCount,
  warningCount,
  loading,
  onRunValidation,
  onShowConfig,
  onOpenConditionStudio,
  canConfigureMappings,
  selectedValidationCount,
  totalValidationCount,
  onOpenRail,
  onToggleDetail,
  showRailToggle = false,
  showDetailToggle = false,
  detailOpen = false,
  focusedSystemName,
  focusedElementCount = 0,
  onClearFocusedSystem,
  capabilityNote,
  onOpenGuide,
  showBackButton = false,
  onGoBack,
  embedded = false,
  tourRefs,
}: ValidationHeaderProps) {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);
  const commandGroupSx = getWorkspaceCommandGroupSx(theme, { density: 'compact' });
  const copy = validationCopy.header;
  const focusedScopeLabel = focusedSystemName
    ? copy.runContext.scope(focusedSystemName, focusedElementCount)
    : null;
  const runContextPrimary = [
    documentName || copy.runContext.currentModel,
    lastValidationLabel ? copy.runContext.lastRun(lastValidationLabel) : null,
  ].filter(Boolean);
  const runContextSecondary = [
    focusedScopeLabel ?? copy.runContext.fullModelScope,
    copy.runContext.checksEnabled(selectedValidationCount, totalValidationCount),
    embedded ? copy.runContext.embeddedMode : null,
  ].filter(Boolean);
  const runContextPrimaryLabel = runContextPrimary.join(' · ');
  const runContextSecondaryLabel = runContextSecondary.join(' · ');
  const compactIconButtonSx = getWorkspaceIconButtonSx(theme);
  const statusConfig = resolveValidationHeaderStatus({
    criticalCount,
    errorCount,
    warningCount,
    unresolvedFamilyCount,
  });
  const isClear = statusConfig.isClear;
  const statusColorValue = isClear
    ? theme.palette.success.main
    : statusConfig.color === 'error'
      ? theme.palette.error.main
      : theme.palette.warning.main;

  return (
    <Box
      data-testid="validation-header-shell"
      sx={getWorkspaceSubpageHeaderSurfaceSx(workspacePalette, { sticky: false, density: 'compact' })}
    >
      <Stack spacing={capabilityNote ? 0.45 : 0}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={layoutGap.row}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          useFlexGap
        >
          <Stack
            direction="row"
            spacing={layoutGap.comfort}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ minWidth: 0, flex: 1 }}
          >
            {showBackButton && onGoBack && (
              <ActionHelpTooltip help={validationActionTooltips.backToValidation}>
                <IconButton
                  aria-label={copy.actionLabels.back}
                  size="small"
                  onClick={onGoBack}
                  sx={compactIconButtonSx}
                >
                  <BackIcon fontSize="small" />
                </IconButton>
              </ActionHelpTooltip>
            )}
            {showRailToggle && onOpenRail && (
              <ActionHelpTooltip help={validationActionTooltips.openTriageRail}>
                <IconButton
                  ref={tourRefs?.railToggleRef}
                  data-testid="validation-tour-related-rail-toggle"
                  size="small"
                  aria-label={copy.actionLabels.openRail}
                  onClick={onOpenRail}
                  sx={compactIconButtonSx}
                >
                  <MenuIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </ActionHelpTooltip>
            )}
            <CardTitle sx={{
              color: workspacePalette.headingText,
              minWidth: 0,
              whiteSpace: { xs: 'normal', sm: 'nowrap' }
            }}>
              {copy.title}
            </CardTitle>
            <ActionHelpTooltip help={validationActionTooltips.validationStatusSummary} placement="bottom">
              <Chip
                icon={renderStatusIcon(statusConfig.icon)}
                label={statusConfig.label}
                size="small"
                variant="outlined"
                sx={{
                  color: statusColorValue,
                  borderColor: alpha(statusColorValue, isClear ? 0.28 : 0.34),
                  backgroundColor: alpha(statusColorValue, theme.palette.mode === 'light' ? 0.055 : 0.1),
                  '& .MuiChip-icon': {
                    color: statusColorValue,
                  },
                  '& .MuiChip-label': {
                    color: statusColorValue,
                  },
                }}
              />
            </ActionHelpTooltip>
            {!isClear && (
              <CaptionText sx={{
                color: workspacePalette.mutedText,
                whiteSpace: 'nowrap',
                display: { xs: 'none', sm: 'block' }
              }}>
                {copy.runContext.totalIssues(totalIssueCount, unresolvedFamilyCount)}
              </CaptionText>
            )}
            <Box
              ref={tourRefs?.runContextRef}
              data-testid="validation-tour-target-header-run-context"
              sx={{
                minWidth: { xs: '100%', sm: 220 },
                flex: { xs: '1 1 100%', md: '1 1 220px' },
                maxWidth: { xs: '100%', md: 640 },
                minHeight: { xs: 40, md: 44 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: layoutGap.micro,
                px: { xs: 0.85, md: 1.05 },
                py: { xs: 0.55, md: 0.7 },
                borderRadius: `${borderRadius.sm}px`,
                border: `1px solid ${workspacePalette.sectionBorder}`,
                backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.92 : 0.3),
              }}
            >
              <CaptionText sx={{
                display: 'block',
                minWidth: 0,
                color: workspacePalette.bodyText,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {runContextPrimaryLabel}
              </CaptionText>
              <CaptionText sx={{
                display: 'block',
                minWidth: 0,
                color: workspacePalette.mutedText,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {runContextSecondaryLabel}
              </CaptionText>
            </Box>
            {focusedScopeLabel && (
              <ActionHelpTooltip help={validationActionTooltips.validationScopeChip} placement="bottom">
                <Chip
                  label={focusedScopeLabel}
                  size="small"
                  variant="outlined"
                  sx={{
                    maxWidth: { xs: '100%', sm: 320 },
                    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.7 : 0.22),
                    '& .MuiChip-label': {
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              </ActionHelpTooltip>
            )}
            {focusedSystemName && onClearFocusedSystem && (
              <ActionHelpTooltip help={validationActionTooltips.clearScope}>
                <Button
                  size="small"
                  onClick={onClearFocusedSystem}
                  sx={{ minWidth: 0, px: 0.5, color: workspacePalette.mutedText }}
                >
                  {copy.scopeAction}
                </Button>
              </ActionHelpTooltip>
            )}
          </Stack>

          <Stack
            ref={tourRefs?.actionsRef}
            data-testid="validation-tour-target-header-actions"
            direction="row"
            spacing={layoutGap.cozy}
            alignItems="center"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            flexWrap="wrap"
            useFlexGap
            sx={[
              commandGroupSx,
              {
                minWidth: 0,
              },
            ]}
          >
            {showDetailToggle && onToggleDetail ? (
              <ActionHelpTooltip help={validationActionTooltips.toggleDetail} placement="bottom">
                <IconButton
                  ref={tourRefs?.detailToggleRef}
                  data-testid="validation-tour-target-header-detail-toggle"
                  size="small"
                  aria-label={detailOpen ? copy.actionLabels.hideDetails : copy.actionLabels.showDetails}
                  onClick={onToggleDetail}
                  sx={compactIconButtonSx}
                >
                  {detailOpen ? <DetailsOpenIcon fontSize="small" /> : <DetailsIcon fontSize="small" />}
                </IconButton>
              </ActionHelpTooltip>
            ) : null}
            {onOpenGuide ? (
              <ActionHelpTooltip help={validationActionTooltips.validationPageGuide} placement="bottom">
                <IconButton
                  ref={tourRefs?.guideActionRef}
                  data-testid="validation-tour-target-header-guide"
                  size="small"
                  aria-label={copy.actionLabels.guide}
                  onClick={onOpenGuide}
                  sx={compactIconButtonSx}
                >
                  <GuideIcon fontSize="small" />
                </IconButton>
              </ActionHelpTooltip>
            ) : null}
            <ActionHelpTooltip
              help={validationActionTooltips.conditionStudio}
              disabled
              disabledHelp="Condition Studio is under development and will be available soon."
              placement="bottom"
            >
              <IconButton
                ref={tourRefs?.conditionStudioActionRef}
                data-testid="validation-tour-target-header-condition-studio"
                size="small"
                aria-label={copy.actionLabels.conditionStudio}
                onClick={onOpenConditionStudio}
                disabled
                sx={compactIconButtonSx}
              >
                <ConditionStudioIcon fontSize="small" />
              </IconButton>
            </ActionHelpTooltip>
            <ValidationHeaderConfigurationActions
              loading={loading}
              canConfigureMappings={canConfigureMappings}
              onShowConfig={onShowConfig}
              configureChecksRef={tourRefs?.configureActionRef}
              buttonSx={compactIconButtonSx}
            />
            <ActionHelpTooltip help={validationActionTooltips.runValidation} placement="bottom">
              <Button
                ref={tourRefs?.runActionRef}
                data-testid="validation-tour-target-header-run"
                size="small"
                variant="contained"
                startIcon={<RunIcon />}
                onClick={onRunValidation}
                disabled={loading || selectedValidationCount === 0}
                sx={[
                  getWorkspaceActionButtonSx(theme, { tone: 'primary', minHeight: 30 }),
                  { px: 1.25 },
                ]}
              >
                {loading ? copy.actionLabels.running : copy.actionLabels.run}
              </Button>
            </ActionHelpTooltip>
          </Stack>
        </Stack>

        {capabilityNote && (
          <CaptionText sx={{ color: workspacePalette.mutedText }}>
            {capabilityNote}
          </CaptionText>
        )}
      </Stack>
    </Box>
  );
}
